import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { safeImgUrl } from "@/lib/images";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

function loadCartFor(userId) {
  if (!userId) return []; // guests never get a persisted cart
  try { return JSON.parse(localStorage.getItem(`kb_cart_${userId}`) || "[]"); }
  catch (parseErr) {
    logger.warn("[cart] Failed to parse stored cart, starting empty:", parseErr);
    return [];
  }
}

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.user_id || null;
  const [items, setItems] = useState(() => []); // starts empty; loaded per-user below
  const lastUserId = useRef(undefined);

  // Whenever the logged-in user changes (login, logout, or switching
  // accounts on a shared device), swap to that user's own cart. Guests
  // (userId === null) always get a fresh, non-persisted, in-memory cart —
  // nothing is ever read from or written to localStorage for them.
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    setItems(loadCartFor(userId));
  }, [userId]);

  useEffect(() => {
    if (userId) {
      localStorage.setItem(`kb_cart_${userId}`, JSON.stringify(items));
    }
  }, [items, userId]);

  const add = useCallback((product, qty = null) => {
    if (!userId) {
      toast.error("Please log in to continue shopping.");
      return;
    }
    const q = qty ?? product.moq ?? 1;
    const stock = product.available_qty ?? Infinity;
    setItems((arr) => {
      const idx = arr.findIndex((it) => it.product_id === product.product_id);
      if (idx >= 0) {
        const copy = [...arr];
        const newQty = Math.min(copy[idx].qty + q, stock);
        copy[idx] = { ...copy[idx], qty: newQty, available_qty: stock };
        return copy;
      }
      return [...arr, {
        product_id: product.product_id, title: product.title,
        price: product.price, qty: Math.min(q, stock), image: safeImgUrl(product.images),
        unit: product.unit, farmer_name: product.farmer_name, available_qty: stock,
      }];
    });
    toast.success(`Added ${product.title} to cart`);
  }, [setItems, userId]);

  const remove = useCallback(
    (pid) => setItems((arr) => arr.filter((it) => it.product_id !== pid)),
    [setItems],
  );
  const updateQty = useCallback(
    (pid, qty) => setItems((arr) => arr.map((it) => {
      if (it.product_id !== pid) return it;
      const stock = it.available_qty ?? Infinity;
      const safeQty = Math.min(Math.max(1, Math.floor(qty) || 1), stock);
      return { ...it, qty: safeQty };
    })),
    [setItems],
  );
  const clear = useCallback(() => setItems([]), [setItems]);

  const total = items.reduce((sum, it) => sum + it.price * it.qty, 0);
  const count = items.reduce((sum, it) => sum + it.qty, 0);

  const value = useMemo(
    () => ({ items, add, remove, updateQty, clear, total, count }),
    [items, add, remove, updateQty, clear, total, count],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
